import { ACCESS } from '@padlet/wall-privacy'
import { __ } from 'bits/intl'

enum PERMISSION {
  WRITABLE = 'can_write',
  READABLE = 'can_read',
  EDITABLE = 'can_edit',
  ADMINABLE = 'can_administer',
  NONE = 'none',
}

// Classes are defined in surface_panel_share.vue
const PERMISSION_COLOR_CLASS = {
  [PERMISSION.READABLE]: 'readable',
  [PERMISSION.EDITABLE]: 'editable',
  [PERMISSION.WRITABLE]: 'writable',
  [PERMISSION.ADMINABLE]: 'adminable',
}

const PERMISSION_CODE_READABLE = 1
const PERMISSION_CODE_WRITABLE = 2
const PERMISSION_CODE_EDITABLE = 4
const PERMISSION_CODE_ADMINABLE = 8

// Privacy options for:
// - Native tenants
const ACCESS_PUBLIC = ACCESS.PUBLIC
const ACCESS_MEMBERS_ONLY = ACCESS.MEMBERS_ONLY
// - Non-Native tenants
const ACCESS_ORGWIDE = ACCESS.ORGWIDE
// - Both
const ACCESS_SECRET = ACCESS.SECRET
const ACCESS_PRIVATE = ACCESS.PRIVATE
const ACCESS_PASSWORD_PROTECTED = ACCESS.PASSWORD_PROTECTED

const PERMISSION_HUMAN_READABLE = __('Can read')
const PERMISSION_HUMAN_WRITABLE = __('Can write')
const PERMISSION_HUMAN_EDITABLE = __('Can edit')
const PERMISSION_HUMAN_ADMINABLE = __('Can administer')

const PERMISSION_DESCRIPTION_READABLE = __(
  "Can view posts. Cannot add posts, edit and approve others' posts, modify and delete padlet, invite collaborators.",
)
const PERMISSION_DESCRIPTION_WRITABLE = __(
  "Can view and add posts. Cannot edit and approve others' posts, modify and delete padlet, invite collaborators.",
)
const PERMISSION_DESCRIPTION_EDITABLE = __(
  "Can view and add posts, edit and approve others' posts. Cannot modify and delete padlet, invite collaborators.",
)
const PERMISSION_DESCRIPTION_ADMINABLE = __(
  "Can view and add posts, edit and approve others' posts, modify and delete padlet, invite collaborators.",
)

const PERMISSION_MAP = {
  [PERMISSION.READABLE]: {
    permission: PERMISSION.READABLE,
    descriptor: PERMISSION_HUMAN_READABLE,
    description: PERMISSION_DESCRIPTION_READABLE,
    code: PERMISSION_CODE_READABLE,
  },
  [PERMISSION.WRITABLE]: {
    permission: PERMISSION.WRITABLE,
    descriptor: PERMISSION_HUMAN_WRITABLE,
    description: PERMISSION_DESCRIPTION_WRITABLE,
    code: PERMISSION_CODE_WRITABLE,
  },
  [PERMISSION.EDITABLE]: {
    permission: PERMISSION.EDITABLE,
    descriptor: PERMISSION_HUMAN_EDITABLE,
    description: PERMISSION_DESCRIPTION_EDITABLE,
    code: PERMISSION_CODE_EDITABLE,
  },
  [PERMISSION.ADMINABLE]: {
    permission: PERMISSION.ADMINABLE,
    descriptor: PERMISSION_HUMAN_ADMINABLE,
    description: PERMISSION_DESCRIPTION_ADMINABLE,
    code: PERMISSION_CODE_ADMINABLE,
  },
}

function getPrivacyText({ organization }: { organization: string }): { [key: string]: string } {
  return {
    ABOUT_SHARING: __(
      'You can always share a padlet with someone by sending its link, via a QR code, broadcasting it, or embedding it somewhere. It makes things really convenient, but that convenience comes at the cost of privacy.',
    ),
    ABOUT_ACCESS: __('Here, you can choose the right combination of sharing convenience and privacy for your padlet:'),
    [ACCESS_PRIVATE]: __(
      'Keep the padlet hidden from the public. Even if someone manages to get to it, they should not be able to access it.',
    ),
    [ACCESS_PASSWORD_PROTECTED]: __(
      'Keep the padlet hidden from the public. If I choose to share it with someone, they should require a password to access it.',
    ),
    [ACCESS_SECRET]: __(
      'Keep the padlet hidden from the public. If I choose to share it with someone, they should be able to access it.',
    ),
    [ACCESS_MEMBERS_ONLY]: __('Keep the padlet hidden from the public. Only logged in users can access this padlet.'),
    [ACCESS_PUBLIC]: __('Let the whole world see this padlet. Put it on my profile.'),
    [ACCESS_ORGWIDE]: __(
      'Keep the padlet hidden from the public. Let anyone from %{organization} access this padlet.',
      { organization },
    ),
    SUBTEXT_MEMBERS: __('Privacy setting doesn’t apply to invited members. They each have their own setting.'),
    SUBTEXT_EMBED: __('Privacy setting applies even when you embed this padlet somewhere.'),
  }
}

const privacyIcon = {
  [ACCESS_PUBLIC]: 'public',
  [ACCESS_SECRET]: 'visibility_off',
  [ACCESS_ORGWIDE]: 'organization',
  [ACCESS_PASSWORD_PROTECTED]: 'key',
  [ACCESS_PRIVATE]: 'lock',
  [ACCESS_MEMBERS_ONLY]: 'organization',
}

const shortAccessName: { [key: string]: string } = {
  [ACCESS_PUBLIC]: __('Public'),
  [ACCESS_SECRET]: __('Secret'),
  [ACCESS_PRIVATE]: __('Private'),
  [ACCESS_ORGWIDE]: __('Org wide'),
  [ACCESS_PASSWORD_PROTECTED]: __('Password'),
  [ACCESS_MEMBERS_ONLY]: __('Members only'),
}
const longAccessName: { [key: string]: string } = {
  [ACCESS_PUBLIC]: __('Public'),
  [ACCESS_SECRET]: __('Secret'),
  [ACCESS_PRIVATE]: __('Private'),
  [ACCESS_ORGWIDE]: __('Organization wide'),
  [ACCESS_PASSWORD_PROTECTED]: __('Password protected'),
  [ACCESS_MEMBERS_ONLY]: __('Members only'),
}

export {
  PERMISSION,
  PERMISSION_MAP,
  PERMISSION_COLOR_CLASS,
  PERMISSION_CODE_READABLE,
  PERMISSION_CODE_WRITABLE,
  PERMISSION_CODE_EDITABLE,
  PERMISSION_CODE_ADMINABLE,
  ACCESS_PUBLIC,
  ACCESS_SECRET,
  ACCESS_PRIVATE,
  ACCESS_ORGWIDE,
  ACCESS_PASSWORD_PROTECTED,
  ACCESS_MEMBERS_ONLY,
  getPrivacyText,
  privacyIcon,
  shortAccessName,
  longAccessName,
}
